// SelectField.tsx
import React from 'react';
import { Link } from 'react-router-dom';
import { Skeleton } from '@radix-ui/themes';
import '../../styles/global.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
	faArrowUpRightFromSquare,
	faChevronDown,
	faTrashAlt,
} from '@fortawesome/free-solid-svg-icons';
import ConfirmDialog from '../ConfirmDialog/ConfirmDialog';

// Option type definition
interface Option {
	key: string;
	label?: string;
}

// Props definition
interface SelectFieldProps {
	label: string; // Le label du champ qui est affiché au-dessus
	selectedKey: string | undefined; // La valeur actuelle du select (c'est à dire la key de l'option choisie)
	options: Option[]; // Les options que l'on peut choisir dans le select (key et label définis dans l'interface Option ci-dessus)
	onValueChange: (value: string) => void;
	error?: string | null;
	visibility?: boolean;
	readOnly?: boolean;
	loading?: boolean;
	optionsLoading?: boolean;
	required?: boolean;
	entityHref?: string; // Lien vers la fiche de l'élément sélectionné
	onClear?: () => void; // Retire l'association, sans supprimer la fiche liée
}

// SelectField component
const SelectField: React.FC<SelectFieldProps> = ({
	label,
	selectedKey,
	onValueChange,
	error,
	options,
	visibility,
	readOnly,
	loading,
	optionsLoading = false,
	required = false,
	entityHref,
	onClear,
}) => {
	// Génération d'un identifiant unique pour l'input
	const selectId = `input-${label}`;
	// Gestion de la visibilité à true si non définie
	const visibilitySelect = visibility ?? true;
	// Gestion de la propriété readOnly
	const readOnlySelect = readOnly ?? false;

	const [open, setOpen] = React.useState(false);
	const [query, setQuery] = React.useState('');
	const [confirmOpen, setConfirmOpen] = React.useState(false);
	const rootRef = React.useRef<HTMLDivElement>(null);
	const inputRef = React.useRef<HTMLInputElement>(null);

	const labelOf = (label?: string) => label ?? '';
	const selectedOption = (options ?? []).find((opt) => opt.key === selectedKey);
	const filteredOptions = (options ?? []).filter((opt) =>
		labelOf(opt.label).toLowerCase().includes(query.trim().toLowerCase())
	);

	// Ferme la liste si le clic est en dehors du champ
	React.useEffect(() => {
		if (!open) return;
		const onPointerDown = (event: MouseEvent) => {
			if (!rootRef.current?.contains(event.target as Node)) {
				setOpen(false);
				setQuery('');
			}
		};
		document.addEventListener('mousedown', onPointerDown);
		return () => document.removeEventListener('mousedown', onPointerDown);
	}, [open]);

	// Focus de la recherche à l'ouverture
	React.useEffect(() => {
		if (open) inputRef.current?.focus();
	}, [open]);

	const close = () => {
		setOpen(false);
		setQuery('');
	};

	const choose = (key: string) => {
		onValueChange(key);
		close();
	};

	const fieldClass = `
		w-full border border-gray-300 p-2 rounded flex justify-between items-center
		bg-gray-100
		focus:outline-none focus:ring-2 focus:ring-blue-500
		${readOnlySelect ? 'text-gray-400 cursor-not-allowed bg-gray-200' : 'text-gray-700 hover:border-gray-400'}
	`;

	return (
		<div style={{ display: visibilitySelect ? 'block' : 'none' }}>
			<label
				htmlFor={selectId}
				className="block text-sm font-medium text-gray-700 mb-1"
			>
				{label}
				{required && <span className="text-gray-600 ml-1">*</span>}
			</label>
			{loading ? (
				<Skeleton
					className="w-full h-10 rounded block bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200"
					style={{
						backgroundSize: '200% 100%',
						animation: 'shimmer 1.5s infinite linear',
					}}
				/>
			) : (
				<div className="flex items-center gap-2">
					<div className="relative flex-1" ref={rootRef}>
						{open && !readOnlySelect ? (
							<input
								id={selectId}
								ref={inputRef}
								value={query}
								onChange={(event) => setQuery(event.target.value)}
								onKeyDown={(event) => {
									if (event.key === 'Escape') close();
								}}
								placeholder="Rechercher..."
								className={fieldClass}
							/>
						) : (
							<button
								id={selectId}
								type="button"
								disabled={readOnlySelect}
								onClick={() => {
									if (!readOnlySelect) setOpen(true);
								}}
								className={fieldClass}
							>
								<span className={selectedOption ? '' : 'text-gray-400'}>
									{selectedOption
										? selectedOption.label?.trim() || 'Sans libellé'
										: 'Sélectionner...'}
								</span>
								<FontAwesomeIcon
									icon={faChevronDown}
									className="text-gray-500"
								/>
							</button>
						)}
						{open && !readOnlySelect && (
							<div className="absolute left-0 right-0 top-full mt-1 bg-white border rounded shadow-lg z-50 max-h-60 overflow-auto">
								{filteredOptions.map((opt) => (
									<button
										key={opt.key}
										type="button"
										onClick={() => choose(opt.key)}
										className="block w-full text-left px-3 py-2 cursor-pointer hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-gray-100"
									>
										{opt.label?.trim() || 'Sans libellé'}
									</button>
								))}
								{filteredOptions.length === 0 && !optionsLoading && (
									<div className="p-4 text-center text-gray-500">
										Aucun résultat
									</div>
								)}
								{optionsLoading && (
									<div className="p-4 text-center text-gray-500">
										Chargement...
									</div>
								)}
							</div>
						)}
					</div>
					{selectedOption && entityHref && (
						<Link
							to={entityHref}
							title="Voir la fiche"
							className="inline-flex text-gray-400 hover:text-blue-500 transition-colors"
						>
							<FontAwesomeIcon icon={faArrowUpRightFromSquare} />
						</Link>
					)}
					{selectedOption && onClear && !required && (
						<button
							type="button"
							title="Retirer l'association"
							onClick={() => setConfirmOpen(true)}
							className="text-gray-400 hover:text-red-500 transition-colors"
						>
							<FontAwesomeIcon icon={faTrashAlt} />
						</button>
					)}
				</div>
			)}
			{error && <p className="text-red-600 text-sm mt-1">{error}</p>}
			<ConfirmDialog
				open={confirmOpen}
				title="Retirer l'association"
				description={`Retirer le lien avec « ${selectedOption?.label ?? ''} » ? Seule l'association entre les deux éléments est retirée. Aucun élément ne sera supprimé.`}
				confirmColor="red"
				onConfirm={() => {
					onClear?.();
					setConfirmOpen(false);
				}}
				onCancel={() => setConfirmOpen(false)}
			/>
		</div>
	);
};

// Export the component
export default SelectField;
